import {cdnImage, cdnUrl} from '@/util/cdnImage';
import {MUIBox, MUIButton, MUIGrid, MUITypography} from '@/components/MUI-Components';
import {IFINCA_META_DATA} from '@/util/metaData/caseStudies';
import Container from '@mui/material/Container';
import dynamic from 'next/dynamic';
import PageHead from '@/components/PageHead';
import {fontSize, fontWeight, lineHeight} from '@/styles/tokens';
const icon = cdnImage('ifinca-images/coffee-bean-icon.webp');
const plantleft = cdnImage('ifinca-images/ifinca-left-img.webp');
const infinca = cdnImage('ifinca-images/ifinca-logo.webp');
const plantimg = cdnImage('ifinca-images/ifinca-right-img.webp');

// ─── sx objects — module-level constants ──────────────────────────────────────
const ifincaBannerContentSx = {
    fontFamily: "'RegularText', sans-serif",
    fontSize: fontSize[24],
    fontWeight: fontWeight.regular,
    color: 'RGB(255, 255, 255)',
    paddingTop: '25px',
    lineHeight: '36px',
    '& span': {color: 'RGB(255, 211, 53)', fontFamily: "'Headers', sans-serif", fontWeight: fontWeight.bold},
    '@media (max-width: 430px)': {fontSize: fontSize[18], textAlign: 'left', lineHeight: '30px', '& span': {fontSize: fontSize[18]}}
} as const;

// Overrides Result's base result-grid-text sizing for this page only; boosted via `&&`
// to outrank the unscoped `.result-grid-text { font-size: 65px !important }` tabScreen
// rule in globals.scss, which shares the same breakpoint range.
const ifincaResultGridTextSx = {
    '@media (max-width: 1199px)': {
        '&&': {
            right: '-40px !important',
            fontSize: '55px !important'
        }
    }
} as const;

const ifincaBannerWrapperSx = {
    backgroundImage: `url('${cdnUrl('ifinca-images/ifinca-bg-img.webp')}')`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    paddingTop: '30px'
} as const;

const ifincaBannerContentWrapperSx = {backgroundColor: 'RGB(51, 115, 97)'} as const;

// glance-img-background had no base rule in globals.scss — only a mobileScreen override
// narrowing its descendant .divder-wrapper padding to 0; replicated here via a nested selector.
const ifincaGlanceImgBackgroundSx = {
    '& .divder-wrapper': {
        '@media (max-width: 430px)': {padding: '0px'}
    }
} as const;

const ifincaResultImgBackgroundSx = {backgroundColor: 'RGB(51, 115, 97) !important', position: 'relative'} as const;
const ifincaCoffeeBottomImgSx = {position: 'absolute', right: '60px', marginTop: '-40px'} as const;

const ifincaAgriAdvSx = {
    backgroundImage: `url('${cdnUrl('ifinca-images/ifinca-short-bg.webp')}')`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    padding: '30px',
    '@media (max-width: 430px)': {padding: '1px'}
} as const;

const ifincaAgriFoodSx = {
    fontFamily: "'Headers', sans-serif",
    fontSize: fontSize[42],
    fontWeight: fontWeight.bold,
    color: 'RGB(255, 255, 255)',
    paddingRight: '260px',
    '& span': {color: 'RGB(237, 232, 0)'},
    '@media (max-width: 768px)': {paddingRight: 0},
    '@media (max-width: 430px)': {fontSize: '26px', lineHeight: lineHeight[40], padding: 0}
} as const;

// case-study-button stays global (also consumed by off-limits case-study-cimet, plus
// case-study-friendspire, out of scope this batch).
const ifincaCaseStudyButtonSx = {
    backgroundColor: 'RGB(237, 232, 0) !important',
    fontSize: `${fontSize[18]} !important`,
    fontFamily: "'Headers', sans-serif !important",
    textTransform: 'capitalize !important',
    borderRadius: '12px !important',
    margin: '12px 0 !important',
    padding: '10px 30px !important',
    boxShadow: 'none !important',
    color: 'RGB(31, 31, 31) !important'
} as const;

// ─── getStaticProps ─────────────────────────────────────────────────────────
export const getStaticProps = async () => {
    const {
        bannerContent,
        bannerStackData,
        brandsData,
        challengelistData,
        helpdata,
        perfectBlockchainData,
        resultfooterData,
        valuedelivered
    } = await import('@/util/contents/infinca-data');

    return {
        props: {
            bannerContent,
            bannerStackData,
            brandsData,
            challengelistData,
            helpdata,
            perfectBlockchainData,
            resultfooterData,
            valuedelivered
        }
    };
};

const Challenge = dynamic(import('@/components/Ui-components/ifinca-challenge'));
const Perfectblockchain = dynamic(import('@/components/Ui-components/perfectblockchain'));
const BostonCoffee = dynamic(import('@/components/Ui-components/ifincabostoncoffee'));
const Result = dynamic(import('@/components/Ui-components/result'));
const Brands = dynamic(import('@/components/Ui-components/reowned-brands'));
const Help = dynamic(import('@/components/Ui-components/havehelp'));
const CaseStudyBanner = dynamic(import('@/components/Ui-components/casestudy-banner'));
const DisplayImage = dynamic(import('@/components/Ui-components/display-image'));
const InfincaCaseStudy = (props: any) => {
    const {
        openForm,
        bannerContent,
        bannerStackData,
        brandsData,
        challengelistData,
        helpdata,
        perfectBlockchainData,
        resultfooterData,
        valuedelivered
    } = props;
    const heading = (
        <>
            Challenge - Building <span>Transparency, Traceability & Efficiency</span> Across Coffee Supply Chain
        </>
    );
    const mapheading = (
        <>
            Apart from <span>transparency and traceability,</span> the platform would also address the following areas:{' '}
        </>
    );
    const coffeeheading = (
        <>
            Ifinca: From POC to Initial Seed Funding by <span>Boston Coffee House & Beyond</span>
        </>
    );
    const resultheading = (
        <>
            Result: <span>Awards</span> Galore, Series <span>Funding</span> & <span>Rapid Traction</span>
        </>
    );
    const brandsheading = (
        <>
            Used by <span>World-Renowned Brands </span> To Fairly Source & Deliver Quality Coffee To Consumers{' '}
        </>
    );
    const helpheading = (
        <>
            Have a <span>Similar Problem Statement?</span> We Can Help
        </>
    );
    const blockchainheading = (
        <>
            The Search For the <span>Perfect Blockchain-Based Technology </span> Team Begins
        </>
    );
    const bannerheading = (
        <>
            A Mobile-First Blockchain Based Solution For <span>Coffee Supply Chain</span>
        </>
    );
    return (
        <MUIBox>
            <PageHead meta={IFINCA_META_DATA} url="/case-study/ifinca" />
            <MUIBox sx={ifincaBannerWrapperSx}>
                <Container maxWidth="lg">
                    <CaseStudyBanner
                        techstack={bannerStackData}
                        product="Ifinca"
                        service="Coffee Supply Chain"
                        bannerContent={bannerContent}
                        heading={bannerheading}
                        backgroundimg=""
                        plantimg={icon}
                    />
                </Container>
            </MUIBox>
            <MUIBox className="bottom-spacing" sx={ifincaBannerContentWrapperSx}>
                <Container maxWidth="lg">
                    <MUIBox className="plant-img">
                        <DisplayImage src={icon} alt="iFinca coffee bean icon" />
                    </MUIBox>
                    <MUIBox className="coffee-bean-img">
                        <DisplayImage src={icon} alt="iFinca coffee bean icon" />
                    </MUIBox>
                    <MUIBox>
                        {bannerContent?.map((data: any, index: number) => (
                            <MUIBox key={index}>
                                <MUITypography sx={ifincaBannerContentSx}>{data}</MUITypography>
                            </MUIBox>
                        ))}
                    </MUIBox>
                </Container>
            </MUIBox>
            <MUIBox>
                <Container>
                    <Challenge
                        heading={heading}
                        subheading=" Alexander Barrett wanted to build a transparency-driven digital platform that would deliver more value to coffee farmers
                    and provide roasters, cafes and consumers complete traceability to the farmgate price.&#8223;"
                        clientDescription="&#8223;If we’re going to make sure farmers get what they need to produce coffee sustainably, we need to figure out how to
                    better distribute value.&rdquo;"
                        image={cdnImage('ifinca-images/ifinca-client.webp')}
                        clientname="Alexander Barrett"
                        clientintro="CEO of iFinca"
                        clientproductimg={infinca}
                        mapheading={mapheading}
                        challengelistData={challengelistData}
                        footerimg={cdnImage('ifinca-images/ifinca-img-1.webp')}
                    />
                </Container>
            </MUIBox>
            <MUIBox>
                <Container maxWidth="lg">
                    <Perfectblockchain heading={blockchainheading} perfectBlockchainData={perfectBlockchainData} />
                </Container>
            </MUIBox>
            <MUIBox>
                <BostonCoffee
                    headerimg={cdnImage('ifinca-images/ifinca-img-2.webp')}
                    heading={coffeeheading}
                    subheading="The Ifinca project got underway as a proof of concept with a 4 months timeline at a fixed pricing. As soon as this POC was launched, the project received $2 million seed funding from Boston Coffee House & Crazy Mocha. "
                    subheadingtwo="But this was just a sign of things to come. Building on that strong foundation laid with the POC, we assigned 8 seasoned blockchain developers for the full-scale development of the project, whose timeline would stretch a full 1 year. "
                    contentdescription="Some of the key attributes of the platform are mentioned below: "
                    contentheading="Value Delivered"
                    valuedelivered={valuedelivered}
                    bostonimg={plantimg}
                    bottomimg={icon}
                    bottomImgSx={ifincaCoffeeBottomImgSx}
                />
            </MUIBox>
            <MUIBox sx={ifincaGlanceImgBackgroundSx}>
                <Container maxWidth="lg">
                    <Result
                        heading={resultheading}
                        resultcontenttwo="In a short span of time, Finca’s user base includes as many as 38 exporters, 14 importers, 26 roasters, and 19 cafes, and 9430 individual farmers and 13 cooperatives across popular coffee growing countries like Colombia, Mexico, Guatemala, El Salvador, Honduras, Nicaragua, Haiti and Dominican Republic. "
                        resultcontent="Our team delivered the full-fledged solution in 2019, well within the set timeline and at an affordable budget. Soon after its launch in 2019, iFinca was awarded the 2019 Best New Product Award in Technology at that year’s Speciality Coffee Association Expo. iFinca has also successfully completed Series A, B, and C rounds of funding. "
                        gridimg={cdnImage('ifinca-images/ifinca-img3.webp')}
                        resultfooterData={resultfooterData}
                        gridImgSx={ifincaResultImgBackgroundSx}
                        gridsidetext="Ifinca"
                        gridTextSx={ifincaResultGridTextSx}
                        plantleft={plantleft}
                        bottomimg={icon}
                        className="challenge-heading"
                    />
                </Container>
            </MUIBox>
            <MUIBox className="brands-wrapper top-space margin bottom-spacing">
                <Container maxWidth="lg">
                    <Brands brandsheading={brandsheading} brandsData={brandsData} />
                </Container>
            </MUIBox>
            <MUIBox>
                <Container maxWidth="lg">
                    <Help heading={helpheading} helpdata={helpdata} className="challenge-heading" />
                </Container>
            </MUIBox>
            <MUIBox className="margin bottom-spacing" sx={ifincaAgriAdvSx}>
                <Container maxWidth="lg">
                    <MUITypography className="margin" sx={ifincaAgriFoodSx}>
                        Transform Your <span> Agri-Food Supply Chain </span> Using Our Market-Ready Solution
                    </MUITypography>
                    <MUIGrid container>
                        <MUIBox className="spectrum-btn">
                            <MUIButton
                                label="Get in Touch"
                                variant="contained"
                                sx={ifincaCaseStudyButtonSx}
                                aria-label="Book Consultation"
                                onClick={openForm}
                            />
                        </MUIBox>
                    </MUIGrid>
                </Container>
            </MUIBox>
        </MUIBox>
    );
};
export default InfincaCaseStudy;
InfincaCaseStudy.Layout = 'Main';
